/**
 * SEO Component - WorkStream
 * Componente para gerenciar meta tags, Open Graph, Twitter Cards e Schema.org
 */
import { useEffect } from 'react'

interface SEOProps {
  title?: string
  description?: string
  keywords?: string[]
  image?: string
  url?: string
  type?: 'website' | 'article' | 'product'
  author?: string
  publishedTime?: string
  modifiedTime?: string
  section?: string
  noIndex?: boolean
  canonical?: string
  structuredData?: object | object[]
}

const DEFAULT_SEO = {
  title: 'WorkStream - Visão que move o trabalho',
  description:
    'Plataforma SaaS de monitoramento inteligente com visão computacional e IA. Ponto facial, análise de produtividade, IA comportamental e verificação de EPIs.',
  keywords: [
    'ponto facial',
    'reconhecimento facial',
    'análise de produtividade',
    'verificação de EPIs',
    'visão computacional',
    'machine learning',
    'IA comportamental',
    'gestão de equipes',
    'compliance NR',
    'segurança do trabalho',
    'monitoramento inteligente',
    'SaaS B2B',
  ],
  image: 'https://workstream.com.br/og-image.png',
  url: 'https://workstream.com.br',
  type: 'website' as const,
  author: 'WorkStream Team',
}

export default function SEO({
  title,
  description = DEFAULT_SEO.description,
  keywords = DEFAULT_SEO.keywords,
  image = DEFAULT_SEO.image,
  url = DEFAULT_SEO.url,
  type = DEFAULT_SEO.type,
  author = DEFAULT_SEO.author,
  publishedTime,
  modifiedTime,
  section,
  noIndex = false,
  canonical,
  structuredData,
}: SEOProps) {
  const fullTitle = title ? `${title} | WorkStream` : DEFAULT_SEO.title
  const keywordsString = keywords.join(', ')

  useEffect(() => {
    // Update document title
    document.title = fullTitle

    // Set meta tags
    setMetaTag('description', description)
    setMetaTag('keywords', keywordsString)
    setMetaTag('author', author)

    // Robots
    if (noIndex) {
      setMetaTag('robots', 'noindex, nofollow')
    } else {
      setMetaTag('robots', 'index, follow')
    }

    // Canonical URL
    if (canonical) {
      setLinkTag('canonical', canonical)
    }

    // Open Graph tags
    setMetaTag('og:title', fullTitle, 'property')
    setMetaTag('og:description', description, 'property')
    setMetaTag('og:image', image, 'property')
    setMetaTag('og:url', url, 'property')
    setMetaTag('og:type', type, 'property')
    setMetaTag('og:site_name', 'WorkStream', 'property')
    setMetaTag('og:locale', 'pt_BR', 'property')

    // Article-specific OG tags
    if (type === 'article') {
      if (publishedTime) {
        setMetaTag('article:published_time', publishedTime, 'property')
      }
      if (modifiedTime) {
        setMetaTag('article:modified_time', modifiedTime, 'property')
      }
      if (section) {
        setMetaTag('article:section', section, 'property')
      }
      if (author) {
        setMetaTag('article:author', author, 'property')
      }
    }

    // Twitter Card tags
    setMetaTag('twitter:card', 'summary_large_image')
    setMetaTag('twitter:title', fullTitle)
    setMetaTag('twitter:description', description)
    setMetaTag('twitter:image', image)
    setMetaTag('twitter:creator', '@workstream')
    setMetaTag('twitter:site', '@workstream')

    // Structured Data (JSON-LD)
    if (structuredData) {
      setStructuredData(structuredData)
    }
  }, [
    fullTitle,
    description,
    keywordsString,
    author,
    image,
    url,
    type,
    publishedTime,
    modifiedTime,
    section,
    noIndex,
    canonical,
    structuredData,
  ])

  return null
}

// Helper functions
function setMetaTag(
  name: string,
  content: string,
  attribute: 'name' | 'property' = 'name'
) {
  let element = document.querySelector(`meta[${attribute}="${name}"]`)

  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(attribute, name)
    document.head.appendChild(element)
  }

  element.setAttribute('content', content)
}

function setLinkTag(rel: string, href: string) {
  let element = document.querySelector(`link[rel="${rel}"]`)

  if (!element) {
    element = document.createElement('link')
    element.setAttribute('rel', rel)
    document.head.appendChild(element)
  }

  element.setAttribute('href', href)
}

function setStructuredData(data: object | object[]) {
  const scriptId = 'structured-data'
  let script = document.getElementById(scriptId) as HTMLScriptElement | null

  if (!script) {
    script = document.createElement('script') as HTMLScriptElement
    script.id = scriptId
    script.setAttribute('type', 'application/ld+json')
    document.head.appendChild(script)
  }

  // Handle array of structured data
  if (script) {
    if (Array.isArray(data)) {
      script.textContent = JSON.stringify(data)
    } else {
      script.textContent = JSON.stringify(data)
    }
  }
}

// Predefined Structured Data Schemas

export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'WorkStream',
  url: 'https://workstream.com.br',
  logo: 'https://workstream.com.br/logo.png',
  description:
    'Plataforma SaaS de monitoramento inteligente com visão computacional e IA para gestão de ponto facial, produtividade e segurança.',
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+55-11-3000-0000',
    contactType: 'customer support',
    email: 'contato@workstream.com.br',
    areaServed: 'BR',
    availableLanguage: ['Portuguese', 'English'],
  },
  sameAs: [
    'https://twitter.com/workstream',
    'https://linkedin.com/company/workstream',
    'https://instagram.com/workstream',
  ],
}

export const softwareApplicationSchema = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'WorkStream',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'BRL',
  },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.9',
    ratingCount: '127',
  },
  description:
    'Plataforma de monitoramento inteligente com visão computacional e IA. Ponto facial, análise de produtividade, IA comportamental e verificação de EPIs.',
}

export const faqSchema = (faqs: { question: string; answer: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
})

export const breadcrumbSchema = (
  items: { name: string; url: string }[]
) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url,
  })),
})

export const productSchema = (product: {
  name: string
  description: string
  price: number
  currency: string
  availability: string
}) => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: product.name,
  description: product.description,
  offers: {
    '@type': 'Offer',
    price: product.price.toString(),
    priceCurrency: product.currency,
    availability: `https://schema.org/${product.availability}`,
  },
})
